// Live engraving at beauty and fragrance launches. Targets (OpenSEO US,
// 2026-11-03): fragrance engraving 1,900, perfume engraving 1,900 (shared
// with the studio page, which owns "perfume bottle engraving"), lipstick
// engraving 880, live engraving / engraving event (low volume, high CPC).
// Investment range, capacity and COI: src/data/event-engraving.js (owner:
// no low price ladder). Bottles: most can be engraved live, tested first;
// CerMark for a dark mark (owner 2026-11-03). Beauty examples are real,
// anonymized studio work (owner 2026-11-03); no live beauty event is claimed.
const {
  eventPricing,
  eventCapacity,
  eventPriceAnswer,
  eventInsurance,
} = require('../event-engraving')
const { onsiteEngravingFacts: onsite } = require('../onsite-engraving')

const beautyEvents = {
  key: 'beautyEvents',
  path: '/services/laser-engraving/beauty-events/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  reviewedNote: 'Investment and capacity from PrintAWorld’s 2026 event quotes',
  title: 'Live Perfume & Lipstick Engraving for Beauty Events | NYC',
  description:
    'Live perfume, fragrance and lipstick engraving for NYC beauty launches and pop-ups. Guests submit names by QR; we engrave bottles and caps on site.',
  crumbs: [
    ['Laser engraving', '/services/laser-engraving/'],
    ['On-site engraving', onsite.path],
  ],
  crumb: 'Beauty events',
  eyebrow: 'Live engraving / beauty and fragrance launches',
  heading:
    'Live perfume, fragrance and lipstick engraving for NYC beauty events.',
  answer: `PrintAWorld runs live laser engraving stations at beauty pop-ups, influencer events, store events and fragrance launches in New York. Guests scan a QR code, type a name or initials, and we engrave it on the perfume bottle, lipstick cap, compact, brush or leather case while they shop the event. Most bottles can be engraved live; we test your exact SKU first. One station engraves about ${eventCapacity.perHour} items an hour, and most brand activations run ${eventPricing.investment}.`,
  cta: 'Get a beauty event quote',
  statusDemo: {
    event: 'Fragrance launch',
    claim: '#027',
    name: 'M.R.',
    wait: '4 ahead of you · about 10 min',
  },
  keyFacts: [
    {
      label: 'Per station',
      value: `${eventCapacity.perHour} / hour`,
      note: 'About 250 in 3 hours with batch loading',
    },
    {
      label: 'Activations',
      value: '$5K–$10K+',
      note: 'Most brand events, plus tax',
    },
    {
      label: 'Your SKU',
      value: 'Tested first',
      note: 'Send 10–15% spares',
    },
    { label: 'Deposit', value: '50%', note: 'Reserves the date' },
  ],
  sections: [
    {
      type: 'cards',
      id: 'items',
      eyebrow: 'What we engrave',
      title: 'Perfume bottles, lipstick caps, compacts and cases.',
      intro:
        'Names, initials, dates or a small icon, engraved in the brand’s approved fonts. Every item is tested on your actual SKU before the event, because finishes and coatings change the result.',
      index: '01 / THE ITEMS',
      items: [
        {
          label: 'Fragrance',
          title: 'Perfume and fragrance bottles',
          body: 'Initials or a name on the bottle or the cap. Most bottles can be engraved live. Glass engraves frosted, or we apply a marking compound (CerMark) for a dark mark. We test your exact bottle first.',
        },
        {
          label: 'Lips',
          title: 'Lipstick caps',
          body: 'Metal and coated caps take a crisp, small name or initials. Great for a lipstick launch or a shade bar.',
        },
        {
          label: 'Face',
          title: 'Compacts and palettes',
          body: 'Mirror compacts, cushion compacts and palette lids, personalized at the counter.',
        },
        {
          label: 'Tools',
          title: 'Brushes',
          body: 'A name on wood or metal brush handles.',
        },
        {
          label: 'Leather',
          title: 'Cosmetic cases and pouches',
          body: 'Leather and faux-leather cosmetic cases, monogrammed on the spot.',
        },
        {
          label: 'Not on site?',
          title: 'Pre-personalized PR mailers',
          body: 'No live station? We engrave or print names on compacts, palettes and bottles at our studio for PR mailers and influencer kits.',
          link: [
            'Beauty products for PR mailers',
            '/services/laser-engraving/beauty-products/',
          ],
        },
      ],
    },
    {
      type: 'steps',
      id: 'how-it-works',
      eyebrow: 'Guest flow',
      title: 'How live engraving works at a beauty event.',
      intro: eventCapacity.summary,
      index: '02 / THE GUEST FLOW',
      items: [
        {
          title: 'Scan the QR',
          body: 'Guests scan the code at the counter. No app, no login, and no email address collected.',
        },
        {
          title: 'Type a name',
          body: 'They type a name or initials and pick from the fonts and icons your team approved, with a live preview.',
        },
        {
          title: 'We batch-engrave',
          body: 'Names land in one queue. We load items in batches and engrave while guests keep shopping, getting makeovers or taking photos.',
        },
        {
          title: 'Pick up when ready',
          body: 'Each guest gets a claim number and a status page that turns to ready. A board at the counter lists finished numbers.',
        },
      ],
    },
    {
      type: 'steps',
      id: 'booking',
      eyebrow: 'Before the event',
      title: 'Test on your SKU, then reserve the date.',
      index: '03 / THE PREP',
      items: [
        {
          title: 'Send the brief',
          body: 'Date, venue, hours, guest count, the products and the fonts or icons guests can choose.',
        },
        {
          title: 'Send products and spares',
          body: 'You supply the products, plus 10–15% spares for testing and for any misspelled name.',
        },
        {
          title: 'Approve the sample',
          body: 'We engrave your actual SKU and send photos of the result and the time per piece.',
        },
        {
          title: 'Reserve the date',
          body: 'A 50% deposit reserves the date; the balance is due before the event.',
        },
      ],
    },
    {
      type: 'pricing',
      id: 'pricing',
      eyebrow: 'Pricing',
      title: 'What a live beauty activation costs.',
      index: '04 / THE PRICE',
      lead: {
        label: 'Most brand activations',
        value: eventPricing.investment,
        note: eventPricing.investmentNote,
      },
      rowsTitle: 'The standard activation',
      rows: eventPricing.rows,
      notes: [
        ...eventPricing.notes,
        'For beauty events the brand usually supplies its own products, plus 10–15% spares.',
      ],
      cta: 'Price my event',
    },
    {
      type: 'cards',
      id: 'beauty-work',
      eyebrow: 'Beauty work from our studio',
      title: 'Personalization we’ve done for beauty brands and PR firms.',
      intro:
        'Studio projects, described without the brand. These were made at our studio, not at a live station.',
      index: '05 / OUR WORK',
      items: [
        {
          title: '30 eyeshadow palettes in 2 days',
          body: 'Names UV printed on 30 eyeshadow palettes for a PR firm, turned around in 2 days.',
        },
        {
          title: 'Logo on mirror compacts',
          body: 'A logo engraved on mirror compacts for a PR mailer.',
        },
        {
          title: 'A luxury cushion compact',
          body: 'A luxury beauty brand’s cushion compact, engraved.',
        },
        {
          title: 'Lipstick-shaped trophies',
          body: 'Lipstick-shaped trophies for a beauty retailer’s creator-program event.',
          link: [
            'Product-shaped trophies',
            '/services/custom-trophy/product-shaped-trophies/',
          ],
        },
      ],
    },
    {
      type: 'contrast',
      id: 'agencies',
      eyebrow: 'For agencies and beauty PR',
      title: 'A personalization moment guests line up for, without the line.',
      paragraphs: [
        'Live engraving gives guests a reason to stop at the counter and a product they keep. The QR queue means nobody stands in line: they shop, get a makeover or take photos while we engrave.',
        'We work from your brief: the SKU, approved fonts and icons, counter layout and event hours. Send what’s decided and what may still change.',
      ],
      cta: 'Plan a beauty event',
    },
  ],
  reviews: 'laser-engraving',
  faqTitle: 'Live beauty engraving questions, answered.',
  faqs: [
    {
      question: 'How much does live perfume engraving at an event cost?',
      answer: eventPriceAnswer,
    },
    {
      question: 'Can you engrave perfume bottles live at our launch?',
      answer:
        'Yes. It depends on the bottle, but most bottles can be engraved live. We test your exact bottle beforehand. Glass engraves to a frosted mark, or we apply a marking compound (CerMark) so it engraves dark.',
    },
    {
      question: 'Can you do lipstick engraving at a beauty event?',
      answer:
        'Yes. Lipstick caps take a small name or initials. We test the cap finish before the event and confirm how many we can do an hour with your design.',
    },
    {
      question: 'How many guests can one engraving station serve?',
      answer: `${eventCapacity.summary} A second station shares the same guest queue.`,
    },
    {
      question: 'Who supplies the products?',
      answer:
        'The brand supplies its products, plus 10–15% spares for testing and replacements. We don’t engrave guests’ own personal items.',
    },
    {
      question: 'Do guests need to download an app?',
      answer:
        'No. Guests scan a QR code and use a web page in their phone’s browser. There’s no login, and the guest app doesn’t collect email addresses.',
    },
    {
      question:
        'Can you personalize products for a PR mailer instead of a live event?',
      answer:
        'Yes. We engrave or UV print names and logos on compacts, palettes, bottles and brushes at our studio and ship them back for your mailer or influencer kits.',
    },
    eventInsurance,
    {
      question: 'How far ahead should we book?',
      answer:
        'Four or more weeks is comfortable, to leave time to test your SKU. If your date is closer, tell us in the brief and we’ll say what’s possible.',
    },
  ],
  quote: {
    eyebrow: 'Let’s plan your station',
    title: 'Tell us about your beauty event.',
    body: 'Send the date, venue, hours, guest count and the products to engrave. We’ll reply with a test plan and a written quote.',
    checklist: [
      'Event date, hours and venue',
      'Expected guest count',
      'The products (SKUs) and how many, plus 10–15% spares',
      'Fonts, icons or logo guests can choose',
      'Any venue or retailer requirements',
    ],
  },
  eventPlanner: true,
  related: [
    ['On-site laser engraving', onsite.path],
    [
      'Luggage tags and hat bar',
      '/services/laser-engraving/luggage-tags-hat-bar/',
    ],
    [
      'Beauty products for PR mailers',
      '/services/laser-engraving/beauty-products/',
    ],
    ['Custom props and giant replicas', '/services/custom-props/'],
  ],
  schema: {
    name: 'Live engraving for beauty and fragrance events',
    alternateName: [
      'Perfume engraving',
      'Fragrance engraving',
      'Lipstick engraving',
      'Live engraving at beauty events',
    ],
    serviceType: 'On-site laser engraving',
    areaServed: { '@type': 'City', name: 'New York City' },
  },
}

module.exports = { beautyEvents }
